import type { DriverDocumentContentType } from '@chukua/common';
import { DRIVER_DOCUMENT_CONTENT_TYPES } from '@chukua/common';
import { Button, DatePicker, Form, Modal, Upload } from 'antd';
import type { Dayjs } from 'dayjs';
import { UploadCloud } from 'lucide-react';
import { useToast } from '../../lib/use-toast';
import type { DriverDocumentType } from './drivers.api';
import type { UploadDriverDocumentInput } from './use-driver-detail';

interface UploadFormValues {
  issuedAt?: Dayjs;
  expiresAt?: Dayjs;
}

interface DriverDocumentUploadModalProps {
  open: boolean;
  documentType: DriverDocumentType | null;
  uploading: boolean;
  onUpload: (input: UploadDriverDocumentInput) => Promise<boolean>;
  onClose: () => void;
}

function isAllowedContentType(type: string): type is DriverDocumentContentType {
  return (DRIVER_DOCUMENT_CONTENT_TYPES as readonly string[]).includes(type);
}

/** Drives the three-call sequence (presign, PUT, confirm) — `onUpload` from `useDriverDetail` does the actual calls. */
export function DriverDocumentUploadModal({ open, documentType, uploading, onUpload, onClose }: DriverDocumentUploadModalProps) {
  const [form] = Form.useForm<UploadFormValues>();
  const toast = useToast();

  async function handleFile(file: File): Promise<boolean> {
    if (!documentType) return false;
    if (!isAllowedContentType(file.type)) {
      toast.error('Choose a PDF, JPEG or PNG file');
      return false;
    }

    const values = form.getFieldsValue();
    const uploaded = await onUpload({
      documentTypeCode: documentType.code,
      file,
      contentType: file.type,
      issuedAt: values.issuedAt?.format('YYYY-MM-DD'),
      expiresAt: values.expiresAt?.format('YYYY-MM-DD'),
    });
    if (uploaded) {
      form.resetFields();
      onClose();
    }
    return false;
  }

  return (
    <Modal open={open} title={documentType ? `Upload ${documentType.name}` : 'Upload document'} onCancel={onClose} footer={null} destroyOnClose>
      <Form<UploadFormValues> form={form} layout="vertical" requiredMark={false} disabled={uploading}>
        <Form.Item name="issuedAt" label="Issued (optional)">
          <DatePicker className="w-full" />
        </Form.Item>
        <Form.Item name="expiresAt" label="Expires (optional)">
          <DatePicker className="w-full" />
        </Form.Item>
        <Upload
          accept="application/pdf,image/jpeg,image/png"
          showUploadList={false}
          disabled={uploading}
          beforeUpload={(file) => {
            void handleFile(file);
            return false;
          }}
        >
          <Button icon={<UploadCloud className="size-3.5" />} loading={uploading}>
            Choose file
          </Button>
        </Upload>
      </Form>
    </Modal>
  );
}
